// ===========================================
// Media Exposure Selector
// Reusable radio group for NO / BLUR / FULL media consent selection
// Also exports MediaExposureDot for use in admin table columns
// ===========================================

import {
  Box,
  FormControl,
  FormControlLabel,
  Radio,
  RadioGroup,
  Stack,
  Tooltip,
  Typography,
} from '@mui/material';
import type { MediaExposure } from '../../api/consent.api';

// ===========================================
// MEDIA EXPOSURE DOT
// ===========================================

const EXPOSURE_COLORS: Record<MediaExposure, string> = {
  NO: '#ff4040',
  BLUR: '#FFCE00',
  FULL: '#96DAC9',
  PENDING: '#9DA5AF',
};

const EXPOSURE_LABELS: Record<MediaExposure, string> = {
  NO: 'No consent',
  BLUR: 'Blurred only',
  FULL: 'Full consent',
  PENDING: 'Pending',
};

export function MediaExposureDot({ exposure }: { exposure: MediaExposure }) {
  return (
    <Tooltip title={`Media consent: ${EXPOSURE_LABELS[exposure]}`} arrow>
      <Box
        sx={{
          width: 12,
          height: 12,
          borderRadius: '50%',
          bgcolor: EXPOSURE_COLORS[exposure],
          display: 'inline-block',
          flexShrink: 0,
        }}
      />
    </Tooltip>
  );
}

// ===========================================
// OPTIONS CONFIG
// ===========================================

const OPTIONS: Array<{
  value: Exclude<MediaExposure, 'PENDING'>;
  label: string;
  description: string;
  color: string;
}> = [
  {
    value: 'NO',
    label: 'No Media Use',
    description:
      "Your child's image will not be used in any school media, photos, or marketing materials.",
    color: '#ff4040',
  },
  {
    value: 'BLUR',
    label: 'Blurred / Anonymised',
    description:
      'Your child may appear in school media but their face will be blurred or anonymised.',
    color: '#FFCE00',
  },
  {
    value: 'FULL',
    label: 'Full Consent',
    description:
      'Your child may appear fully in photos and videos used for school marketing and social media.',
    color: '#96DAC9',
  },
];

// ===========================================
// SELECTOR COMPONENT
// ===========================================

interface MediaExposureSelectorProps {
  value: Exclude<MediaExposure, 'PENDING'> | null;
  onChange: (value: Exclude<MediaExposure, 'PENDING'>) => void;
  disabled?: boolean;
}

export default function MediaExposureSelector({
  value,
  onChange,
  disabled = false,
}: MediaExposureSelectorProps) {
  return (
    <FormControl component="fieldset" disabled={disabled} sx={{ width: '100%' }}>
      <RadioGroup
        value={value ?? ''}
        onChange={(e) => onChange(e.target.value as Exclude<MediaExposure, 'PENDING'>)}
      >
        <Stack spacing={1.5}>
          {OPTIONS.map((opt) => (
            <Box
              key={opt.value}
              sx={{
                border: '1px solid',
                borderColor: value === opt.value ? opt.color : 'divider',
                borderRadius: 1.5,
                p: 1.5,
                cursor: disabled ? 'default' : 'pointer',
                bgcolor: value === opt.value ? `${opt.color}14` : 'transparent',
                transition: 'all 0.15s ease',
                '&:hover': disabled ? {} : { borderColor: opt.color, bgcolor: `${opt.color}0a` },
              }}
              onClick={() => !disabled && onChange(opt.value)}
            >
              <FormControlLabel
                value={opt.value}
                control={
                  <Radio
                    size="small"
                    sx={{ color: opt.color, '&.Mui-checked': { color: opt.color } }}
                  />
                }
                label={
                  <Stack direction="row" alignItems="center" spacing={1}>
                    <Box
                      sx={{
                        width: 12,
                        height: 12,
                        borderRadius: '50%',
                        bgcolor: opt.color,
                        flexShrink: 0,
                      }}
                    />
                    <Box>
                      <Typography variant="body2" fontWeight={600}>
                        {opt.label}
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        {opt.description}
                      </Typography>
                    </Box>
                  </Stack>
                }
                sx={{ m: 0, width: '100%' }}
              />
            </Box>
          ))}
        </Stack>
      </RadioGroup>
    </FormControl>
  );
}
